import { QueryResult, useApolloClient } from '@apollo/client';
import { DocumentNode, Kind, OperationDefinitionNode } from 'graphql';
import { produce } from 'immer';
import { useCallback, useMemo } from 'react';
import { QueryVariables } from 'src/graphql/types';

type Updater<Query> = (existing: Query) => Query | void | undefined;

function inferRootFieldName(query: DocumentNode): string | undefined {
  const opDef = query.definitions.find(
    (d): d is OperationDefinitionNode => d.kind === Kind.OPERATION_DEFINITION,
  );
  const sel = opDef?.selectionSet?.selections?.[0];
  if (sel?.kind === Kind.FIELD) {
    return sel.alias?.value ?? sel.name?.value;
  }

  return undefined;
}

export type CacheResetOptions = {
  fieldName?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  args?: Record<string, any>;
  refetch?: boolean;
};

export type CacheManager<Query> = {
  update: (handler: Updater<Query>) => void;
  reset: (opts?: CacheResetOptions) => void;
  client: ReturnType<typeof useApolloClient>;
};

export function useCacheManager<Query, Vars extends QueryVariables>(
  query: DocumentNode,
  queryResult: QueryResult<Query, Vars>,
): CacheManager<Query> {
  const client = useApolloClient();

  const { variables, updateQuery } = queryResult;

  const update = useCallback(
    (handler: Updater<Query>) => {
      try {
        updateQuery((existing) => {
          if (existing) {
            // use immer produce method to provide draft state of cache for immutable updates
            return produce(existing, handler) ?? existing;
          }
          return existing;
        });
      } catch (e) {
        console.error('[useCacheManager] updateQuery failed', { query, variables, error: e });
      }
    },
    [query, updateQuery, variables],
  );

  const reset = useCallback(
    (opts?: CacheResetOptions) => {
      const fieldName = opts?.fieldName ?? inferRootFieldName(query);

      const args = opts?.args ?? { queryParams: variables?.params };

      client.cache.evict({ id: 'ROOT_QUERY', fieldName, args });
      client.cache.gc();

      if (opts?.refetch) {
        void client.refetchQueries({ include: [query] });
      }
    },
    [client, query, variables?.params],
  );

  return useMemo(() => ({ update, reset, client }), [update, reset, client]);
}
